/* ==========================================================================
   subscribe-modal.js
   --------------------------------------------------------------------------
   Drives every instance of snippets/subscribe-modal.liquid on the page:
     • Opens / closes <dialog data-subscribe-modal> elements
     • Toggles between SMS (phone) and Email modes
     • AJAX-submits both forms to Klaviyo /client/subscriptions/
     • Renders inline error / success state per form without reloading

   Pairing contract (markup):
     <dialog data-subscribe-modal id="...">                  — the modal
       <form data-channel="email|sms"                        — one form per
             data-klaviyo-company="..."                        channel; the
             data-klaviyo-list="..."                           same modal can
             data-success-message="...">                       hold one of
         <input name="email|phone">                            each.
         <small data-subscribe-status></small>
         <button type="submit">
       </form>
       <button data-subscribe-toggle>                        — swap channels
       <button data-subscribe-close>                         — close modal
     </dialog>
     <button data-subscribe-open="<modal-id>">               — open trigger
                                                               (any element
                                                               on the page)

   Multiple modals can coexist on the same page; each opener targets by id.
   ========================================================================== */

(function () {
  'use strict';

  /* Klaviyo API revision header pin. Older revisions (pre-2025) rejected
     the `subscriptions` block under profile.attributes — the error came
     back as "'subscriptions' is not a valid field for the resource
     'profile'". 2026-04-15 is the latest stable revision and matches the
     current schema (verified against Klaviyo's OpenAPI spec). Update only
     after testing against a real signup; Klaviyo bumps the schema with
     each revision and the consent block shape can shift. */
  var KLAVIYO_REVISION = '2026-04-15';
  var ENDPOINT_BASE    = 'https://a.klaviyo.com/client/subscriptions/';

  /* US-only lenient phone normalizer. Strips non-digits, drops a leading
     1, prepends +1. Returns null when fewer than 10 digits remain. Switch
     to libphonenumber-js if non-US numbers ever need to be accepted. */
  function normalizeUSPhone(raw) {
    if (!raw) return null;
    var digits = String(raw).replace(/\D+/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (digits.length !== 10) return null;
    return '+1' + digits;
  }

  /* Minimal email sanity check. The <input type="email"> already does the
     HTML5 pass; this catches obvious cases like missing @ or trailing dot
     before we round-trip to Klaviyo. Lean — not RFC 5322. */
  function isLikelyEmail(raw) {
    if (!raw) return false;
    var trimmed = String(raw).trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  }

  function showStatus(statusEl, message, kind) {
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.hidden = false;
    statusEl.classList.remove(
      'antilabs-subscribe-modal__msg--error',
      'antilabs-subscribe-modal__msg--success'
    );
    if (kind === 'error') statusEl.classList.add('antilabs-subscribe-modal__msg--error');
    if (kind === 'success') statusEl.classList.add('antilabs-subscribe-modal__msg--success');
    /* Mirror the state onto data-type so attribute-selector CSS in
       custom-footer.css can target the same treatment. The class
       modifiers are kept for back-compat with any rule already in
       the wild. */
    if (kind === 'error' || kind === 'success') {
      statusEl.setAttribute('data-type', kind);
    } else {
      statusEl.removeAttribute('data-type');
    }
  }

  function clearStatus(statusEl) {
    if (!statusEl) return;
    statusEl.textContent = '';
    statusEl.hidden = true;
    statusEl.classList.remove(
      'antilabs-subscribe-modal__msg--error',
      'antilabs-subscribe-modal__msg--success'
    );
    statusEl.removeAttribute('data-type');
  }

  /* Build Klaviyo subscriptions payload for either channel. The shape is
     identical except for the contact field + consent block, so we keep
     the wiring in one place.

     Source attribution lands in TWO places:
       1. subscription.attributes.custom_source — Klaviyo shows this on
          the subscription event so it's filterable in event-based flows
          ("Subscribed to List" trigger > filter by custom source).
       2. profile.attributes.properties.signup_source — Klaviyo merges
          this onto the profile so it persists across subscriptions and
          is queryable in segments / list filters by property value.
     Same string in both, so you can attribute either way. */
  /* Website experiments (Remy, 2026-10-26). The visitor's arms ("upsell-01:B",
     window.antiExp.value) ride on the profile as anti_exp, so a signup can be
     compared by version; only when the visitor has arms. Same endpoint, same
     shape: one more key under properties. */
  function experimentArms() {
    try {
      var v = window.antiExp && window.antiExp.vid && window.antiExp.value;
      return typeof v === 'string' && /^[a-z0-9-]{1,32}:[A-Z](,[a-z0-9-]{1,32}:[A-Z])*$/.test(v) && v.length <= 256 ? v : '';
    } catch (_) { return ''; }
  }

  /* anti:signup {form, ok} once per submit that reached Klaviyo. form is the
     form's source label as a stable slug (footer_modal_email, …). */
  function publishSignup(sourceLabel, ok) {
    try {
      var form = String(sourceLabel).toLowerCase().replace(/[^a-z0-9_-]+/g, '_').slice(0, 40);
      if (window.antiExp && typeof window.antiExp.pub === 'function') window.antiExp.pub('anti:signup', { form: form, ok: !!ok });
    } catch (_) { /* tracking never breaks a signup */ }
  }

  function buildPayload(channel, identifier, listId, sourceLabel) {
    var profileProperties = { signup_source: sourceLabel };
    var arms = experimentArms();
    if (arms) profileProperties.anti_exp = arms;
    var profileAttrs;
    if (channel === 'sms') {
      profileAttrs = {
        phone_number: identifier,
        properties: profileProperties,
        subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
      };
    } else {
      profileAttrs = {
        email: identifier,
        properties: profileProperties,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };
    }
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: sourceLabel,
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
  }

  function initForm(form) {
    var channel    = form.dataset.channel;
    var companyId  = form.dataset.klaviyoCompany;
    var listId     = form.dataset.klaviyoList;
    var successMsg = form.dataset.successMessage || "Thanks — you're on the list.";
    var input      = form.querySelector(channel === 'sms' ? 'input[type="tel"]' : 'input[type="email"]');
    var button     = form.querySelector('button[type="submit"]');
    var statusEl   = form.querySelector('[data-subscribe-status]');
    if (!channel || !companyId || !listId || !input || !button) return;

    /* Source label — set per-form via the snippet's source_label param
       (data-source attribute). Falls back to the modal id when the
       snippet caller didn't pass one. Channel is appended so footer
       email vs footer SMS land distinctly in Klaviyo. */
    var modal = form.closest('[data-subscribe-modal]');
    var baseSource = form.dataset.source || (modal && modal.id) || 'subscribe-modal';
    var sourceLabel = baseSource + '_' + channel;

    /* Clear stale messages as soon as the visitor edits — matches the
       native HTML5 validation feel for both channels. */
    input.addEventListener('input', function () { clearStatus(statusEl); });

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      var raw = input.value;
      var identifier;
      if (channel === 'sms') {
        identifier = normalizeUSPhone(raw);
        if (!identifier) {
          showStatus(statusEl, 'Please enter a valid US phone number.', 'error');
          input.focus();
          return;
        }
      } else {
        if (!isLikelyEmail(raw)) {
          showStatus(statusEl, 'Please enter a valid email address.', 'error');
          input.focus();
          return;
        }
        identifier = String(raw).trim();
      }

      var originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Subscribing…';
      clearStatus(statusEl);

      var payloadBody = JSON.stringify(buildPayload(channel, identifier, listId, sourceLabel));
      var endpoint = ENDPOINT_BASE + '?company_id=' + encodeURIComponent(companyId);
      var fetchOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: payloadBody
      };

      /* Attempt the POST and unwrap the response into a uniform shape so
         the retry layer can reason about it. Klaviyo returns 202 Accepted
         for queued subscriptions even when the profile already exists;
         treat any 2xx as success. JSON parse may fail on edge cold-start
         (HTML 5xx page) — catch + return null so we still surface SOMETHING. */
      function attempt() {
        return fetch(endpoint, fetchOptions)
          .then(function (response) {
            if (response.ok || response.status === 202) {
              return { ok: true };
            }
            return response.json()
              .catch(function () { return null; })
              .then(function (body) {
                var firstError = body && body.errors && body.errors[0];
                return {
                  ok: false,
                  status: response.status,
                  body: body,
                  detail: firstError && (firstError.detail || firstError.title),
                  retryable: response.status >= 500 || response.status === 0
                };
              });
          })
          .catch(function (err) {
            /* Network-level rejection from fetch — covers both genuine
               network errors AND a Klaviyo-specific quirk: when their
               edge intermittently returns 5xx, the response omits CORS
               headers, so the browser blocks the JS from seeing the
               status code and surfaces it as TypeError: "Failed to fetch".
               These ALL retry because the underlying cause (edge cold
               start, transient outage, or dropped connection) is usually
               cleared within a second or two. */
            return {
              ok: false,
              networkError: { name: err && err.name, message: err && err.message },
              retryable: true
            };
          });
      }

      /* Up to 3 total attempts (initial + 2 retries) with exponential
         backoff at 300ms then 800ms. Klaviyo's edge sometimes returns
         5xx for several seconds in a row before recovering; a single
         300ms retry wasn't long enough to ride out a real outage.
         NOT retried: 4xx client errors (validation, rate limit,
         malformed payload) — those won't change between attempts.
         Klaviyo /client/subscriptions/ is idempotent on (email, list)
         and (phone, list), so a retry landing on top of a previously-
         successful-but-misreported first attempt does not duplicate. */
      var retryDelays = [300, 800];

      function attemptWithRetry(attemptNumber) {
        return attempt().then(function (result) {
          if (result.ok) return result;
          /* eslint-disable no-console */
          console.error('[subscribe-modal] Attempt ' + (attemptNumber + 1) + ' failed', {
            channel: channel,
            status: result.status,
            networkError: result.networkError,
            body: result.body,
            note: result.networkError ? 'TypeError: Failed to fetch — likely Klaviyo edge returned 5xx without CORS headers' : null
          });
          /* eslint-enable no-console */
          if (!result.retryable) return result;
          if (attemptNumber >= retryDelays.length) return result;
          return new Promise(function (resolve) {
            setTimeout(resolve, retryDelays[attemptNumber]);
          }).then(function () { return attemptWithRetry(attemptNumber + 1); });
        });
      }

      attemptWithRetry(0)
        .then(function (final) {
          publishSignup(sourceLabel, final.ok);
          if (final.ok) {
            showStatus(statusEl, successMsg, 'success');
            input.value = '';
            return;
          }
          /* Distinguish three failure modes for the user:
             1. Klaviyo returned a 4xx with a specific message — surface it.
             2. Network / 5xx exhaustion — Klaviyo is unreachable, tell the
                visitor it's our service that's wobbling and to retry shortly.
             3. Unknown — fallback to generic copy. */
          var detail;
          if (final.detail) {
            detail = final.detail;
          } else if (final.networkError || (final.status && final.status >= 500)) {
            detail = "Our signup service is temporarily unavailable. Please try again in a moment.";
          } else {
            detail = "Could not subscribe right now. Please try again.";
          }
          showStatus(statusEl, detail, 'error');
        })
        .then(function () {
          button.disabled = false;
          button.textContent = originalLabel;
        });
    });
  }

  function initModal(modal) {
    /* Two render modes share this init path:
        1. <dialog data-subscribe-modal>  — the popup case. Close button +
           backdrop click both fire modal.close().
        2. <div  data-subscribe-modal>    — the inline "coming soon" PDP
           case. No close button, no backdrop. The form, toggle, and
           AJAX submit all still init normally.
       The dialog-only behaviors guard on showModal availability so the
       inline mount doesn't throw when modal.close() doesn't exist. */
    var isDialog = typeof modal.showModal === 'function';

    var toggle       = modal.querySelector('[data-subscribe-toggle]');
    var phoneWrap    = modal.querySelector('[data-mode="phone"]');
    var emailWrap    = modal.querySelector('[data-mode="email"]');
    var phoneDisc    = modal.querySelector('[data-disclaimer-phone]');
    var emailDisc    = modal.querySelector('[data-disclaimer-email]');
    var phoneLabel   = toggle && toggle.querySelector('[data-toggle-from-phone]');
    var emailLabel   = toggle && toggle.querySelector('[data-toggle-from-email]');

    function setMode(mode) {
      var isEmail = mode === 'email';
      if (phoneWrap) phoneWrap.classList.toggle('antilabs-subscribe-modal__form-wrap--hidden', isEmail);
      if (emailWrap) emailWrap.classList.toggle('antilabs-subscribe-modal__form-wrap--hidden', !isEmail);
      if (phoneDisc) phoneDisc.hidden = isEmail;
      if (emailDisc) emailDisc.hidden = !isEmail;
      if (phoneLabel) phoneLabel.hidden = isEmail;
      if (emailLabel) emailLabel.hidden = !isEmail;
      if (toggle) toggle.setAttribute('aria-pressed', String(isEmail));
    }

    if (isDialog) {
      modal.querySelectorAll('[data-subscribe-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { modal.close(); });
      });

      /* Backdrop click closes the modal. <dialog> reports the dialog element
         itself as the target when the visitor clicks the backdrop area. */
      modal.addEventListener('click', function (e) {
        if (e.target === modal) modal.close();
      });
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        var currentlyPhone = phoneWrap && !phoneWrap.classList.contains('antilabs-subscribe-modal__form-wrap--hidden');
        setMode(currentlyPhone ? 'email' : 'phone');
      });
    }

    /* Initialize submission for every form inside this modal. */
    modal.querySelectorAll('form[data-channel]').forEach(initForm);
  }

  function bootAll() {
    /* Initialize every modal on the page. */
    document.querySelectorAll('[data-subscribe-modal]').forEach(initModal);

    /* Wire openers — each opener carries the target modal's id in
       data-subscribe-open. Lookup is global so a card in custom-home-grid
       can open a modal rendered anywhere on the page. */
    function openFor(el, event) {
      var targetId = el.getAttribute('data-subscribe-open');
      if (!targetId) return;
      var modal = document.getElementById(targetId);
      if (modal && typeof modal.showModal === 'function') {
        event.preventDefault();
        modal.showModal();
      }
    }

    document.querySelectorAll('[data-subscribe-open]').forEach(function (opener) {
      /* Marked so the delegated fallback below knows this one is already live.
         A JS property, deliberately — cloneNode copies ATTRIBUTES but not
         properties, so a clone lands unmarked and the fallback picks it up. */
      opener.__ccSubscribeBound = true;
      opener.addEventListener('click', function (event) { openFor(opener, event); });
    });
  }

  /* DELEGATED FALLBACK (2026-08-27) — openers that appear AFTER boot.
     The direct wiring above runs once at DOMContentLoaded, so any opener
     created later is dead. That is not hypothetical: custom-home-sourcemap
     builds its mobile card mirror with cloneNode(true), which carries markup
     and attributes but NOT listeners — an <a href> survives that because
     navigation is native, a <button> does not. The map's "Notify Me" buttons
     live in exactly that mirror.

     Guarded on the property so an opener bound directly does not fire twice.
     Registered at module scope, not inside bootAll, so it survives re-boots. */
  document.addEventListener('click', function (event) {
    var el = event.target && event.target.closest && event.target.closest('[data-subscribe-open]');
    if (!el || el.__ccSubscribeBound) return;
    var targetId = el.getAttribute('data-subscribe-open');
    if (!targetId) return;
    var modal = document.getElementById(targetId);
    if (modal && typeof modal.showModal === 'function') {
      event.preventDefault();
      modal.showModal();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
